import React from 'react'

export type RadialProgressProps = {
  /** Progress value from 0 - 100 */
  value?: number
  /** Diameter of the radial progress. When `auto`, it fills the parent height. */
  size?: number | 'auto'
  /** Stroke width of the progress arc */
  strokeWidth?: number

  /** Accessible label for screen readers */
  ariaLabel?: string
  /** Optional custom label rendered at the center */
  label?: React.ReactNode
  /** Animate stroke updates */
  animate?: boolean

  //the color palette variant
  variant?: 'success' | 'danger' | 'warning' | 'info' | 'primary' | 'secondary' | 'light' | 'dark'

  now?: number
  max?: number
}

const clampValue = (value: number) => {
  if (Number.isNaN(value)) return 0
  if (value < 0) return 0
  if (value > 100) return 100
  return value
}

export const RadialProgress = ({
  value,
  size = 'auto',
  strokeWidth = 7,
  ariaLabel,
  label,
  animate = true,
  variant = 'primary',
  now,
  max,
}: RadialProgressProps) => {
  const progressColor = `var(--bs-${variant})`
  // The arc is a fill and takes the variant's fill token; the centre label is text, so it takes the
  // foreground token — `--bs-link-color` for primary (styling guide §3), the `-text-emphasis` pair
  // for the rest. A fill token as text loses contrast in dark mode (theme-colour.md).
  const labelColor = variant === 'primary' ? 'var(--bs-link-color)' : `var(--bs-${variant}-text-emphasis)`
  const trackColor = `var(--bs-${variant}-bg-subtle)`

  max = max ?? 0
  now = now ?? 0
  const percentage = max > 0 ? now / max : 0
  value = value ?? percentage * 100

  const clampedValue = clampValue(value)
  const resolvedSize = size === 'auto' ? undefined : size
  const viewBoxSize = resolvedSize ?? 100
  const center = viewBoxSize / 2
  const radius = center - strokeWidth / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference * (1 - clampedValue / 100)

  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(clampedValue)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={ariaLabel}
      style={{
        width: resolvedSize ?? '100%',
        height: resolvedSize ?? '100%',
        display: 'inline-grid',
        placeItems: 'center',
      }}
    >
      <svg
        width={resolvedSize ?? '100%'}
        height={resolvedSize ?? '100%'}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        style={{ gridArea: '1 / 1', pointerEvents: 'none' }}
      >
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke={trackColor}
          strokeWidth={strokeWidth}
          transform={`rotate(-90 ${center} ${center})`}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="transparent"
          stroke={progressColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${center} ${center})`}
          style={{ transition: animate ? 'stroke-dashoffset 0.4s ease' : undefined }}
        />
      </svg>
      <div
        style={{
          gridArea: '1 / 1',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: (resolvedSize ?? 100) * 0.35,
          fontWeight: 600,
          color: labelColor,
          pointerEvents: 'none',
        }}
      >
        <span>{label ?? Math.round(clampedValue)}</span>
      </div>
    </div>
  )
}
